import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import dictionary from "./dictionary";
import api from "../api/api";

export const LANGUAGES = [
  { code: "az", label: "AZ", name: "Azərbaycan" },
  { code: "en", label: "EN", name: "English" },
  { code: "ru", label: "RU", name: "Русский" },
];

export const DEFAULT_LANG = "az";
const STORAGE_KEY = "quizzo_lang";
const LOCALES = { az: "az-AZ", en: "en-US", ru: "ru-RU" };

const supported = (code) => LANGUAGES.some((l) => l.code === code);

/** Brauzer dilini oxu, dəstəklənmirsə az-a düş */
const detectLang = () => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (supported(saved)) return saved;

  const browser = (navigator.language || "").slice(0, 2).toLowerCase();
  return supported(browser) ? browser : DEFAULT_LANG;
};

/**
 * Backend-dən gələn çoxdilli sahəni (məs. sual başlığı {az,en,ru}) cari dildə qaytarır.
 * Sahə sadə mətndirsə olduğu kimi qaytarılır — beləliklə tərcümə olunmayan
 * sahələr (mağaza adı, kampaniya başlığı) də problemsiz işləyir.
 */
export const translateValue = (value, lang = DEFAULT_LANG) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);

  // fallback zənciri: seçilən dil → az → en → ru → ilk dolu dəyər
  const order = [lang, DEFAULT_LANG, "en", "ru"];
  for (const code of order) {
    if (value[code]) return value[code];
  }

  const first = Object.values(value).find((v) => typeof v === "string" && v);
  return first || "";
};

/** "Salam {name}" + {name: "Əli"} → "Salam Əli" */
const interpolate = (text, vars) => {
  if (!vars) return text;
  return Object.keys(vars).reduce(
    (acc, key) => acc.replaceAll(`{${key}}`, String(vars[key] ?? "")),
    text
  );
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectLang);
  // Admin panelindən idarə olunan mətnlər — dictionary-nin üstünü örtür
  const [remoteTexts, setRemoteTexts] = useState({});

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { data } = await api.get("ui-texts");
        if (!cancelled) setRemoteTexts(data?.texts || {});
      } catch {
        // Backend əlçatmazdırsa lokal dictionary ilə davam edilir
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const setLang = useCallback((code) => {
    if (supported(code)) setLangState(code);
  }, []);

  const t = useCallback(
    (key, vars) => {
      const entry = remoteTexts[key] || dictionary[key];
      if (!entry) return key; // açar tapılmadı — açarın özünü göstər (debug üçün)

      return interpolate(translateValue(entry, lang), vars);
    },
    [lang, remoteTexts]
  );

  const tr = useCallback((value) => translateValue(value, lang), [lang]);

  const formatDate = useCallback(
    (value, options = { dateStyle: "medium" }) => {
      if (!value) return "-";
      return new Date(value).toLocaleString(LOCALES[lang] || LOCALES.az, options);
    },
    [lang]
  );

  const value = useMemo(
    () => ({ lang, setLang, t, tr, formatDate, locale: LOCALES[lang] || LOCALES.az }),
    [lang, setLang, t, tr, formatDate]
  );

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
};

/** Komponentlərdə: const { t, tr, lang, setLang } = useI18n(); */
export const useI18n = () => {
  const ctx = useContext(LanguageContext);

  if (!ctx) {
    throw new Error("useI18n yalnız <LanguageProvider> daxilində istifadə oluna bilər");
  }

  return ctx;
};
